import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Component, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { NavigationError, Router, RouterOutlet } from '@angular/router';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { BootFallbackComponent } from '@core/components/boot-fallback/boot-fallback.component';
import { BookingConfirmationStateService } from '@core/state/booking-confirmation-state.service';
import { OneSvgDefsComponent } from '@one/components/one-svg-defs/one-svg-defs.component';
import { BookingConfirmationComponent } from '@pages/booking/components/booking-confirmation/booking-confirmation.component';
import { LogErrorService } from '@shared/services/log-error.service';
import dayjs from 'dayjs';
import ptBR from 'dayjs/locale/pt-br';
import updateLocale from 'dayjs/plugin/updateLocale';
import { provideLottieOptions } from 'ngx-lottie';
import { filter, take } from 'rxjs/operators';
import packageJson from '../../package.json';

dayjs.extend(updateLocale);

@Component({
  selector: 'agj-root',
  imports: [RouterOutlet, BookingConfirmationComponent, OneSvgDefsComponent, BootFallbackComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [
    provideLottieOptions({
      player: () => import(/* webpackChunkName: 'lottie-web' */ 'lottie-web')
    })
  ]
})
export class AppComponent implements OnInit {
  public readonly bookingConfirmation = inject(BookingConfirmationStateService);
  private readonly platform = inject(PLATFORM_ID);
  private swUpdate = inject(SwUpdate);
  private LogErrorService = inject(LogErrorService);
  private router = inject(Router);

  public ngOnInit(): void {
    this.updateDayJsLocale();

    this.logProjectVersion();
    this.checkForUpdates();
    this.handleChunkLoadError();
  }
  private handleChunkLoadError(): void {
    if (!isPlatformBrowser(this.platform)) return;

    this.router.events
      .pipe(filter((event: any): event is NavigationError => event instanceof NavigationError))
      .subscribe((errorEvent: NavigationError) => {
        // Esse cada serve para os detectar quando o usuario tenta navegar por uma rota que está sem chunk no server
        if (
          errorEvent.error &&
          (errorEvent.error.message?.includes('Failed to fetch dynamically imported module') ||
            errorEvent.error.message?.includes('Importing a module script failed') ||
            errorEvent.error.message?.includes('Failed to load module script') ||
            errorEvent.error.message?.includes('is not a valid JavaScript MIME type') ||
            errorEvent.error.message?.includes('ChunkLoadError'))
        ) {
          setTimeout(() => {
            if (errorEvent?.target?.url) {
              try {
                location.replace(errorEvent?.target?.url);
              } catch (e) {
                location.reload();
              }
            } else location.reload();
          }, 0);
        }
      });
  }

  private checkForUpdates(): void {
    if (isPlatformBrowser(this.platform) && this.swUpdate?.isEnabled) {
      this.swUpdate.checkForUpdate().catch((err) => this.LogErrorService.error('Erro ao verificar atualização do PWA checkForUpdate', err));

      this.swUpdate.versionUpdates
        .pipe(
          filter((event: VersionEvent) => event?.type === 'VERSION_READY'),
          take(1)
        )
        .subscribe({
          next: () => document.location.reload(),
          error: (err) => {
            this.LogErrorService.error('Erro ao verificar atualização do PWA:', err);
          }
        });
    }
  }

  private logProjectVersion(): void {
    if (isPlatformServer(this.platform)) return;

    console.log(
      `%cAgendaJá App v${packageJson.version}`,
      'font-size: 50px; font-weight: bold; background-color: #8200ff; color: white); padding: 20px'
    );
  }

  // TODO V2: Quando incluirmos o módulo de tradução, levar isso para lá.
  private updateDayJsLocale(): void {
    dayjs.locale('pt-br', ptBR);
  }
}
